import { useState } from "react";
import { Link } from "react-router-dom";
import SavingsCalculator from "@/components/SavingsCalculator";

function PricingPage() {
  const [showCalculator, setShowCalculator] = useState(false);
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link to="/" className="flex items-center mr-4">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold">SG</span>
                </div>
              </Link>
              <h1 className="text-xl font-bold">Pricing</h1>
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/"
                className="text-blue-600 hover:text-blue-700 font-medium">
                Home
              </Link>
              <Link
                to="/how-it-works"
                className="text-blue-600 hover:text-blue-700 font-medium">
                How It Works
              </Link>
              <button
                onClick={() => setShowCalculator(true)}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700">
                Calculate Savings
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              No hidden fees. No surprises. Know exactly what You'll pay before
              you ship.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
              <p className="text-blue-800 font-semibold text-lg">
                💡 <strong>Free to start:</strong> Create account, get Delaware
                address, no monthly fees
              </p>
            </div>
          </div>

          {/* Pricing Overview */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-12">
            <div className="bg-blue-600 text-white text-center py-6">
              <h3 className="text-2xl font-bold mb-2">Pay Per Package</h3>
              <p className="text-sm opacity-90">
                Only when you ship, never monthly fees
              </p>
            </div>

            <div className="p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-semibold mb-4 text-lg">Shipping Costs</h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="font-medium">Base shipping rates:</span>
                      <span className="font-semibold text-blue-600">
                        $8-25 per package
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="font-medium">
                        Consolidation savings:
                      </span>
                      <span className="font-semibold text-green-600">
                        $10-50+ per group
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="font-medium">
                        International delivery:
                      </span>
                      <span className="font-semibold">$5 flat fee</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="font-medium">
                        Storage (after 45 days):
                      </span>
                      <span className="font-semibold">$1/day</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-4 text-lg">
                    Optional Services
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="font-medium">Insurance:</span>
                      <span className="font-semibold">$2-8 (1% of value)</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="font-medium">Package repackaging:</span>
                      <span className="font-semibold text-green-600">
                        $0 (free)
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="font-medium">Priority processing:</span>
                      <span className="font-semibold">+$3 (within 24hrs)</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="font-medium">
                        Live customer support:
                      </span>
                      <span className="font-semibold text-green-600">
                        $0 (free)
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg mt-8">
                <h5 className="font-semibold mb-4 text-green-800">
                  Transparency Guarantee
                </h5>
                <div className="flex items-center justify-center text-green-700 font-medium">
                  🔒 No hidden fees • 🎯 Money-back guarantee • 📞 Free support
                  • 🇨🇦 Canadian owned
                </div>
              </div>
            </div>
          </div>

          {/* Comparison Table */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-12">
            <div className="bg-green-600 text-white text-center py-6">
              <h3 className="text-2xl font-bold mb-2">
                Why ShopGlobal Saves You Money
              </h3>
              <p className="text-sm opacity-90">
                Compare: Direct shipping vs ShopGlobal Pro
              </p>
            </div>

            <div className="p-8">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Traditional Shipping */}
                <div className="border-2 border-red-200 rounded-lg p-6 bg-red-50">
                  <h4 className="font-bold text-xl mb-4 text-red-800 text-center">
                    Traditional International Shipping
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>3 separate packages:</span>
                      <span className="font-semibold text-red-700">$75</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sales tax paid:</span>
                      <span className="font-semibold text-red-700">$14</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Customs/brokerage:</span>
                      <span className="font-semibold text-red-700">$25</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t-2 border-red-300 pt-3">
                      <span>Total cost:</span>
                      <span className="text-red-700">$114</span>
                    </div>
                  </div>
                </div>

                {/* ShopGlobal Shipping */}
                <div className="border-2 border-green-200 rounded-lg p-6 bg-green-50">
                  <h4 className="font-bold text-xl mb-4 text-green-800 text-center">
                    ShopGlobal Pro Solution
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>1 consolidated shipment:</span>
                      <span className="font-semibold text-green-700">$19</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sales tax saved:</span>
                      <span className="font-semibold text-green-700">-$14</span>
                    </div>
                    <div className="flex justify-between">
                      <span>International handling:</span>
                      <span className="font-semibold text-green-700">$5</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t-2 border-green-300 pt-3">
                      <span>Total cost:</span>
                      <span className="text-green-700">$10</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg mt-8">
                <div className="text-center">
                  <div className="text-4xl font-bold text-green-600 mb-2">
                    67% Savings
                  </div>
                  <div className="text-green-800 font-semibold">
                    $104 saved on this example
                  </div>
                  <div className="text-sm text-gray-600 mt-2">
                    Average customer saves $200-500 monthly • 7,842 Canadians
                    already saving thousands
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Real Pricing Examples */}
          <div className="mb-12">
            <h3 className="text-3xl font-bold text-center mb-12">
              Real Customer Examples
            </h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="text-center mb-6">
                  <div className="text-2xl mb-2">📱</div>
                  <h4 className="font-bold text-lg">iPhone Accessories</h4>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span>AirPods case + charger:</span>
                    <span>$89.99 item + shipping</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-200 pt-2 font-semibold">
                    <span>Direct shipping:</span>
                    <span>$89.99 + $39.99 = $129.98</span>
                  </div>
                  <div className="flex justify-between font-semibold text-green-700">
                    <span>ShopGlobal Pro:</span>
                    <span>$89.99 + $12.99 (-$27 savings)</span>
                  </div>
                  <div className="bg-green-50 p-3 rounded mt-4">
                    <div className="text-green-800 font-bold text-center">
                      $27 savings (21% off)
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="text-center mb-6">
                  <div className="text-2xl mb-2">👟</div>
                  <h4 className="font-bold text-lg">Nike Running Shoes</h4>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span>Nike Air Max ($129.99):</span>
                    <span>+$25 shipping + $11.40 tax</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-200 pt-2 font-semibold">
                    <span>Direct shipping:</span>
                    <span>$129.99 + $25 + $11.40 = $166.39</span>
                  </div>
                  <div className="flex justify-between font-semibold text-green-700">
                    <span>ShopGlobal Pro:</span>
                    <span>$129.99 + $18 shipping (-$7.41 tax)</span>
                  </div>
                  <div className="bg-green-50 p-3 rounded mt-4">
                    <div className="text-green-800 font-bold text-center">
                      $18.41 savings (11% off)
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="text-center mb-6">
                  <div className="text-2xl mb-2">💻</div>
                  <h4 className="font-bold text-lg">Laptop Accessories</h4>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span>Charge cord + mouse ($69.98):</span>
                    <span>+$15.99 shipping + $4.90 tax</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-200 pt-2 font-semibold">
                    <span>Direct shipping:</span>
                    <span>$69.98 + $15.99 + $4.90 = $90.87</span>
                  </div>
                  <div className="flex justify-between font-semibold text-green-700">
                    <span>ShopGlobal Pro:</span>
                    <span>$69.98 + $8.99 shipping (-$4.90 tax)</span>
                  </div>
                  <div className="bg-blue-50 p-3 rounded mt-4">
                    <div className="text-green-800 font-bold text-center">
                      $13.91 savings (15% off)
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Package Consolidation Savings */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-12">
            <div className="bg-purple-600 text-white text-center py-6">
              <h3 className="text-2xl font-bold mb-2">
                Package Consolidation Savings
              </h3>
              <p className="text-sm opacity-90">
                Buy from multiple stores once, pay shipping once
              </p>
            </div>

            <div className="p-8">
              <p className="text-gray-700 mb-6 text-lg text-center">
                Shopping from 3 different US stores? Instead of paying 3
                shipping fees, we combine everything into 1 shipment.
              </p>

              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="font-semibold mb-4 text-red-800">
                      Traditional Approach:
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>UPS Standard shipping charge:</span>
                        <span>$28.99 × 3 packages = $86.97</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Separate brokerage fees:</span>
                        <span>$15 × 3 = $45</span>
                      </div>
                      <div className="flex justify-between font-bold border-t border-gray-300 pt-2">
                        <span>Total paid to carriers:</span>
                        <span className="text-red-600">$131.97</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-4 text-green-800">
                      ShopGlobal Consolidation:
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>1 UPS Standard shipment:</span>
                        <span>$32.99</span>
                      </div>
                      <div className="flex justify-between">
                        <span>ShopGlobal service fee:</span>
                        <span>$7.50</span>
                      </div>
                      <div className="flex justify-between">
                        <span>International handling:</span>
                        <span>$5.00</span>
                      </div>
                      <div className="flex justify-between font-bold border-t border-gray-300 pt-2">
                        <span>Total cost:</span>
                        <span className="text-green-600">$45.49</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg mt-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600 mb-1">
                      $86.48 Savings
                    </div>
                    <div className="text-green-800 font-semibold mb-2">
                      65% less than shipping separately!
                    </div>
                    <p className="text-sm text-gray-600">
                      ℹ️ Consolidation savings increase with number of packages
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tax Savings */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-12">
            <div className="bg-orange-500 text-white text-center py-6">
              <h3 className="text-2xl font-bold mb-2">Delaware Tax Savings</h3>
              <p className="text-sm opacity-90">
                No sales tax = automatic savings on every purchase
              </p>
            </div>

            <div className="p-8">
              <p className="text-gray-700 mb-6 text-center">
                Delaware has <strong>no sales tax</strong>, so you save 5-15% on
                purchases compared to other states.
              </p>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center bg-gray-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600 mb-1">
                    $129.99
                  </div>
                  <div className="text-sm text-gray-600 mb-2">
                    Nike Shoes (Ontario tax 13%)
                  </div>
                  <div className="font-semibold text-green-600">
                    $16.90 saved
                  </div>
                </div>

                <div className="text-center bg-gray-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600 mb-1">
                    $299.99
                  </div>
                  <div className="text-sm text-gray-600 mb-2">
                    Computer Monitor (BC tax 12%)
                  </div>
                  <div className="font-semibold text-green-600">
                    $35.99 saved
                  </div>
                </div>

                <div className="text-center bg-gray-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-orange-600 mb-1">
                    $89.99
                  </div>
                  <div className="text-sm text-gray-600 mb-2">
                    Bluetooth Speaker (QC tax 14.975%)
                  </div>
                  <div className="font-semibold text-green-600">
                    $13.48 saved
                  </div>
                </div>

                <div className="text-center bg-gray-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-red-600 mb-1">
                    $538.96
                  </div>
                  <div className="text-sm text-gray-600 mb-2">
                    Average monthly savings
                  </div>
                  <div className="font-semibold text-red-600">Per customer</div>
                </div>
              </div>
            </div>
          </div>

          {/* Final CTA */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-700 rounded-xl shadow-xl p-12 text-center text-white">
            <h3 className="text-4xl font-bold mb-6">Ready to Start Saving?</h3>
            <p className="text-xl mb-8 opacity-90">
              Transparent pricing, no surprises, guaranteed savings. Calculate
              your potential savings today.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center mb-8">
              <button
                onClick={() => setShowCalculator(true)}
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 shadow-lg">
                Calculate My Savings
              </button>
              <Link
                to="/signup"
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-600">
                Get Free Delaware Address
              </Link>
            </div>
            <p className="text-sm opacity-75">
              ✦ Free to start ✦ No monthly fees ✦ Cancel anytime ✦ 24/7 support
              ✦
            </p>
          </div>
        </div>
      </div>

      {/* Calculator Modal */}
      {showCalculator && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="relative">
            <button
              onClick={() => setShowCalculator(false)}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-xl">
              ✕
            </button>
            <SavingsCalculator />
          </div>
        </div>
      )}
    </div>
  );
}

export default PricingPage;
